import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { OidcSecurityService, LoginResponse } from 'angular-auth-oidc-client';


/**
 * Single shared 401 -> refresh -> retry pipeline for the whole app.
 *
 * On 401 the first request kicks off `forceRefreshSession`; any other
 * requests that 401 while that refresh is in flight wait on the shared
 * BehaviorSubject and replay once the new token arrives. Without this,
 * each concurrent 401 used to fire its own independent refresh against
 * Keycloak (race), and the OIDC library's localStorage state could end
 * up inconsistent enough that subsequent retries kept getting 401s
 * until the user manually reloaded the page.
 *
 * Mirrors the pattern used in innkeeper-ui http-interceptor.service.ts.
 */
@Injectable()
export class AuthHttpIntercepter implements HttpInterceptor {

    private refreshInProgress = false;
    // null = no refresh result yet (waiters keep waiting)
    // string = new access token (waiters replay)
    // false (a sentinel value) = refresh failed (waiters error out)
    private refreshedToken$ = new BehaviorSubject<string | null | false>(null);

    constructor(private oidcSecurityService: OidcSecurityService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // Smartystreets calls go straight through without auth -- the
        // service expects its own clientAuthId in the URL, no Bearer.
        if (request.url.toLowerCase().indexOf('api.smartystreets.com') > -1) {
            return next.handle(request);
        }

        if (!request.headers.has('Content-Type')) {
            request = request.clone({ headers: request.headers.set('Content-Type', 'application/json') });
        }

        request = request.clone({
            headers: request.headers.set('Accept', 'application/json')
        });

        return this.attempt(request, next, /* isRetry */ false);
    }

    /**
     * Sends the request with whatever access token the OIDC library reports
     * right now. On 401, hands off to refreshThenRetry. The isRetry flag
     * guards against an infinite loop if a freshly-issued token is also
     * rejected.
     */
    private attempt(request: HttpRequest<any>, next: HttpHandler, isRetry: boolean): Observable<HttpEvent<any>> {
        return this.oidcSecurityService.getAccessToken().pipe(
            take(1),
            switchMap(token => {
                const authReq = this.addBearer(request, token);
                return next.handle(authReq).pipe(
                    catchError((error: HttpErrorResponse) => {
                        if (error.status !== 401) {
                            // RxJS 6 throwError takes the error directly --
                            // NOT a factory (() => error). Caught by spec.
                            return throwError(error);
                        }
                        if (isRetry) {
                            // Already retried once with a fresh token and still 401.
                            // Refresh token is dead too -- send to login.
                            this.redirectToLogin();
                            return throwError(error);
                        }
                        return this.refreshThenRetry(request, next, error);
                    })
                );
            })
        );
    }

    /**
     * Shared refresh queue. The first request to land here flips the
     * `refreshInProgress` flag, broadcasts a null on `refreshedToken$`
     * (so latecomers know to wait), then triggers `forceRefreshSession`.
     * Any other 401s that arrive while the refresh is in flight subscribe
     * to `refreshedToken$` and replay their request as soon as a string
     * token is emitted. If the refresh fails or returns no token, we emit
     * `false` so waiters error out instead of hanging forever.
     */
    private refreshThenRetry(
        request: HttpRequest<any>,
        next: HttpHandler,
        originalError: HttpErrorResponse,
    ): Observable<HttpEvent<any>> {
        if (this.refreshInProgress) {
            return this.refreshedToken$.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => {
                    if (token === false) {
                        // Refresh failed in the leader; the leader has already
                        // called redirectToLogin(). Just bubble the original
                        // 401 so call sites see a consistent error.
                        return throwError(originalError);
                    }
                    return next.handle(this.addBearer(request, token));
                }),
            );
        }

        this.refreshInProgress = true;
        // Park waiters until we have a result.
        this.refreshedToken$.next(null);

        return this.oidcSecurityService.forceRefreshSession().pipe(
            take(1),
            catchError(() => {
                this.finishRefresh(false);
                this.redirectToLogin();
                return throwError(originalError);
            }),
            switchMap((result: LoginResponse) => {
                if (!result?.isAuthenticated || !result?.accessToken) {
                    this.finishRefresh(false);
                    this.redirectToLogin();
                    return throwError(originalError);
                }
                this.finishRefresh(result.accessToken);
                // This request itself: retry with isRetry=true so a persistent
                // 401 doesn't loop forever.
                return this.attempt(request, next, true);
            })
        );
    }

    /**
     * Releases the refresh lock and broadcasts the result to any waiters.
     * Then resets the BehaviorSubject to null so the NEXT refresh cycle
     * starts clean (waiters from a future cycle won't see the previous
     * cycle's value).
     */
    private finishRefresh(result: string | false): void {
        this.refreshInProgress = false;
        this.refreshedToken$.next(result);
        // Reset for the next cycle. The microtask delay ensures any
        // currently-subscribed waiters get a chance to see `result` before
        // the BehaviorSubject is reset.
        queueMicrotask(() => this.refreshedToken$.next(null));
    }

    private addBearer(request: HttpRequest<any>, token: string | null | undefined): HttpRequest<any> {
        if (!token) return request;
        return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    private redirectToLogin(): void {
        // Don't start a new auth flow while a callback is still being processed
        // (URL has code/state) -- that would overwrite the in-flight state and
        // surface as "could not find matching config for state".
        const search = window.location.search;
        if (search.indexOf('code=') !== -1 || search.indexOf('state=') !== -1) {
            return;
        }

        this.oidcSecurityService.authorize(undefined, {
            customParams: { prompt: 'select_account' }
        });
    }
}
